import React, { Dispatch, SetStateAction, useEffect } from "react";
import { Card } from "../../global/Card/Card";
import Loading from "../../global/Loading";
import { useRouter } from "next/router";
import { PlanDetails } from "../../../utils/user";
import { useAuth } from "../../../context/authContext";
import { CheckoutMode } from "../../../utils/stripe";
import {
  getStripe,
  getSubscriptionCheckoutSession,
} from "../../../lib/stripeRequest";
import { useToast } from "../../../context/toastContext";

interface PlanCardsProps {
  activeTab: boolean;
  toggleTab: (index: boolean) => void;
  loadingPlans: boolean;
  consumerPlans: Array<any>;
  wineIndustryPlans: any;
  discountPlans?: { planId: number; discount: number; discountCode: string }[];
  setConsumerPlans: Dispatch<SetStateAction<any[]>>;
  setWineIndustryPlans: Dispatch<SetStateAction<any>>;
}

export const PlanCards = ({
  activeTab,
  toggleTab,
  loadingPlans,
  consumerPlans,
  wineIndustryPlans,
  discountPlans,
  setConsumerPlans,
  setWineIndustryPlans
}: PlanCardsProps) => {
  const router = useRouter();
  const toast = useToast();
  const {
    state: { user, subscription },
  } = useAuth();
  const [subscriptionText, setSubscriptionText] = React.useState<string>("");

  const handleOnClickCard = async (
    planId: string,
    onlyDetails?: boolean,
    discounts?: Array<string>
  ) => {
    if (onlyDetails) {
      router.push(`/statics/commercial-subscriptions`);
    } else {
      if (user && !subscription) {
        redirectToStripe(planId, discounts);
      } else {
        const basePath = `subscription?price_id=${planId}`;
        const path = discounts
          ? `${basePath}&discounts=${JSON.stringify(discounts)}`
          : basePath;
        router.push(path);
      }
    }
  };

  const redirectToStripe = async (planId: string, discounts?: Array<string>) => {
    try {
      if (activeTab) {
        const newPlans = consumerPlans.map((plan) => {
          if (plan.stripe_id === planId) {
            return { ...plan, isRedirecting: true };
          }
          return plan;
        });
        setConsumerPlans(newPlans);
      } else {
        let wineIndustryPlansCopy = {...wineIndustryPlans}; // create a copy of the state

        for (let key in wineIndustryPlansCopy) {

          if (wineIndustryPlansCopy[key].stripe_id === planId) {
            wineIndustryPlansCopy[key] = {...wineIndustryPlansCopy[key], isRedirecting: true};
            break;
          }
        }
        setWineIndustryPlans(wineIndustryPlansCopy);
      }

      const body = {
        email: user.email.toLowerCase(),
        mode: CheckoutMode.subscription,
        price_id: planId,
        cancel_url: window.location.href,
        discounts: discounts?.length > 0 ? discounts : null,
      };
      const { data } = await getSubscriptionCheckoutSession(body);
      const stripe = await getStripe();
      await stripe.redirectToCheckout({ sessionId: data.id });
    } catch {
      toast("error", "An error occurred redirecting to Stripe");
    }
  };

  const handlePurchaseGift = (planId: string, discounts?: Array<string>) => {
    const basePath = `gift?price_id=${planId}`;
    const path = discounts
      ? `${basePath}&discounts=${JSON.stringify(discounts)}`
      : basePath;
    router.push(path);
  };

  useEffect(() => {
    setSubscriptionText(
      activeTab
        ? "Choose the plan that is best for you below. "
        : "Choose the plan that is best for your organization below. "
    );
  }, [activeTab]);

  return (
    <>
      <div className="pb-4 text-center flex-col">
        <h1 className="text-2xl	font-bold	">
          Which subscription plan is best for you?
        </h1>
        <p>
          Vinous subscribers have access to our archive of over 500,000 wine
          reviews plus thousands of articles, videos and more from the
          world&rsquo;s most trusted wine critics. {subscriptionText}
          {!activeTab ? (
            <>
              Review the{" "}
              <a href={"/statics/commercial_subscriptions_july_2023_update"}>
                commercial subscriptions page
              </a>{" "}
              for full details.
            </>
          ) : (
            <></>
          )}
        </p>
      </div>
      <div className="flex justify-center p-2">
        <div className="flex justify-center space-x-4 xs:w-full md:w-4/12 bg-slate-200 m-4 p-1 h-18 rounded">
          <button
            className={`p-3 rounded w-60 ${
              activeTab ? "text-white bg-red" : ""
            } `}
            onClick={() => toggleTab(true)}
          >
            I am a consumer
          </button>
          <button
            className={`p-3 rounded w-60 ${
              !activeTab ? "text-white bg-red" : ""
            } `}
            onClick={() => toggleTab(false)}
          >
            I work in the Wine Industry
          </button>
        </div>
      </div>
      {loadingPlans ? (
        <Loading isLoading={loadingPlans} />
      ) : (
        <div>
          <div className={`${activeTab ? "block" : "hidden p-5"}`}>
            <div className="h-1/2 flex justify-center sm:flex-col lg:flex-row md:flex-row xs:flex-col">
              {consumerPlans.length > 0 &&
                consumerPlans.map((plan) => {
                  const recurring = plan.years === 1 ? "year" : "month";
                  const discountsByPlan = discountPlans?.filter(
                    (planDiscount) => planDiscount.planId === plan.id
                  );
                  const discounts = discountsByPlan.reduce((acc, curr) => {
                    return acc + (curr.discount || 0);
                  }, 0);
                  const planDiscountsCodes =
                    discountsByPlan.length > 0
                      ? discountsByPlan.map(
                          (planDiscount) => planDiscount.discountCode
                        )
                      : null;
                  const price = `US$${plan.price - discounts}/${recurring}`;
                  const features = plan.features.split(/\r?\n\n/);
                  return (
                    <Card
                      key={plan.id}
                      headerLabel={plan.name}
                      price={price}
                      descriptions={features}
                      buttonLabel="Subscribe"
                      handleOnClick={() =>
                        handleOnClickCard(
                          plan.stripe_id,
                          false,
                          planDiscountsCodes
                        )
                      }
                      handleOnClickGift={() =>
                        handlePurchaseGift(plan.stripe_id, planDiscountsCodes)
                      }
                      isRedirecting={plan.isRedirecting}
                    />
                  );
                })}
            </div>
          </div>
          <div className={`${!activeTab ? "block" : "hidden p-5"}`}>
            <div className="h-1/2 flex justify-center sm:flex-col lg:flex-row md:flex-row xs:flex-col">
              {Object.values(wineIndustryPlans).map((plan: PlanDetails) => {
                const recurring = "year";
                const price = plan.price ? `US$${plan.price}/${recurring}` : "";
                const features = plan.features;
                return (
                  <Card
                    key={plan.id}
                    headerLabel={plan.name}
                    price={price}
                    descriptions={features}
                    buttonLabel={
                      !plan.onlyDetails ? "Subscribe" : "Details Here"
                    }
                    handleOnClick={() =>
                      handleOnClickCard(plan.stripe_id, plan.onlyDetails)
                    }
                    isRedirecting={plan.isRedirecting}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
